import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User, Star, Flame, Award, Calendar, ChevronRight,
  LogOut, HelpCircle, Bell, Loader2
} from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import useAuthStore from '../../store/authStore';
import studentService from '../../services/studentService';

// Sample user data (fallback for demo)
const SAMPLE_USER_DATA = {
  name: 'Kamau James',
  avatar: 'seedling',
  school_name: 'Green Valley Primary',
  grade: 'Grade 5',
  total_points: 1950,
  current_week: 3,
  streak_days: 8,
  longest_streak: 12,
  activities_completed: 24,
  badges_earned: 4,
  created_at: '2026-01-15',
};

const AVATAR_OPTIONS = ['seedling', 'sprout', 'flower', 'tree', 'boy', 'girl'];

export default function Profile() {
  const { logout } = useAuth();
  const { student, user, isAuthenticated } = useAuthStore();
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);

  // Merge student data from auth store with any additional profile data
  const userData = profileData || student || SAMPLE_USER_DATA;
  const [selectedAvatar, setSelectedAvatar] = useState(userData.avatar || 'seedling');

  // Fetch additional profile data if authenticated
  useEffect(() => {
    const fetchProfile = async () => {
      if (!isAuthenticated || !student) {
        return;
      }

      try {
        setLoading(true);
        // Try to get game data which includes more details
        const data = await studentService.getMyGameData();
        setProfileData({
          ...student,
          ...data.student,
          activities_completed: data.activities_completed || 0,
          badges_earned: data.badges_earned || 0,
        });
      } catch (err) {
        console.error('Failed to fetch profile data:', err);
        // Use auth store data as fallback
        setProfileData(student);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [isAuthenticated, student]);

  const handleLogout = () => {
    logout();
  };

  const handleAvatarChange = async (avatar) => {
    setSelectedAvatar(avatar);
    setShowAvatarPicker(false);

    // Try to update avatar on server
    if (isAuthenticated && student) {
      try {
        await studentService.updateMyAvatar(avatar);
        setProfileData((prev) => prev ? { ...prev, avatar } : null);
      } catch (err) {
        console.error('Failed to update avatar:', err);
      }
    }
  };

  // Get values with fallback for different naming conventions
  const getValue = (camelCase, snakeCase, defaultVal = 0) => {
    return userData[camelCase] ?? userData[snakeCase] ?? defaultVal;
  };

  const stats = [
    {
      label: 'Points',
      value: getValue('totalPoints', 'total_points', 0),
      icon: Star,
      color: 'text-sunshine-500',
    },
    {
      label: 'Streak',
      value: `${getValue('streakDays', 'streak_days', 0)} days`,
      icon: Flame,
      color: 'text-orange-500',
    },
    {
      label: 'Badges',
      value: getValue('badgesEarned', 'badges_earned', 0),
      icon: Award,
      color: 'text-purple-500',
    },
    {
      label: 'Week',
      value: getValue('currentWeek', 'current_week', 1),
      icon: Calendar,
      color: 'text-blue-500',
    },
  ];

  const menuItems = [
    { label: 'Notifications', icon: Bell, path: '/app/notifications' },
    { label: 'Help & Support', icon: HelpCircle, path: '/app/help' },
  ];

  const getAvatarEmoji = (avatar) => {
    const emojis = {
      boy: '👦',
      girl: '👧',
      seedling: '🌱',
      sprout: '🌿',
      tree: '🌳',
      flower: '🌻',
      bee: '🐝',
    };
    return emojis[avatar] || '🌱';
  };

  const currentWeek = getValue('currentWeek', 'current_week', 1);
  const activitiesCompleted = getValue('activitiesCompleted', 'activities_completed', 0);
  const longestStreak = getValue('longestStreak', 'longest_streak', 0);
  const joinedAt = userData.joinedAt || userData.created_at || new Date().toISOString();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-leaf-500 mx-auto mb-2" />
          <p className="text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* Profile Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        {/* Avatar */}
        <button
          onClick={() => setShowAvatarPicker(!showAvatarPicker)}
          className="relative mx-auto mb-4"
        >
          <div className="w-24 h-24 rounded-full bg-leaf-100 flex items-center justify-center
                         ring-4 ring-leaf-200 hover:ring-leaf-300 transition-all">
            <span className="text-5xl">{getAvatarEmoji(selectedAvatar)}</span>
          </div>
          <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-leaf-500 text-white
                         flex items-center justify-center shadow-lg">
            <User size={16} />
          </div>
        </button>

        {/* Avatar Picker */}
        {showAvatarPicker && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex justify-center gap-3 mb-4 p-4 bg-white rounded-2xl shadow-lg"
          >
            {AVATAR_OPTIONS.map((avatar) => (
              <button
                key={avatar}
                onClick={() => handleAvatarChange(avatar)}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl
                           transition-all ${
                             selectedAvatar === avatar
                               ? 'bg-leaf-100 ring-2 ring-leaf-500'
                               : 'bg-gray-100 hover:bg-gray-200'
                           }`}
              >
                {getAvatarEmoji(avatar)}
              </button>
            ))}
          </motion.div>
        )}

        {/* Name & Info */}
        <h1 className="text-2xl mb-1">{userData.name}</h1>
        <p className="text-gray-600">{userData.school_name || userData.school}</p>
        <p className="text-sm text-gray-500">{userData.grade}</p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card p-4 text-center"
            >
              <Icon size={24} className={`mx-auto mb-2 ${stat.color}`} />
              <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Progress Summary */}
      <div className="card p-5 mb-6">
        <h3 className="font-heading text-lg text-leaf-800 mb-4">Your Progress</h3>
        <div className="space-y-4">
          {/* Activities */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Activities Completed</span>
              <span className="font-semibold">{activitiesCompleted}</span>
            </div>
          </div>

          {/* Week Progress */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Yearly Progress</span>
              <span className="font-semibold">{Math.round((currentWeek / 36) * 100)}%</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-bar-fill"
                style={{ width: `${(currentWeek / 36) * 100}%` }}
              />
            </div>
          </div>

          {/* Longest Streak */}
          <div className="flex justify-between items-center pt-2 border-t border-gray-100">
            <span className="text-gray-600 text-sm">Longest Streak</span>
            <span className="font-semibold flex items-center gap-1">
              <Flame size={16} className="text-orange-500" />
              {longestStreak} days
            </span>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="card divide-y divide-gray-100 mb-6">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-leaf-50 flex items-center justify-center">
                <Icon size={20} className="text-leaf-600" />
              </div>
              <span className="flex-1 font-medium">{item.label}</span>
              <ChevronRight size={20} className="text-gray-400" />
            </Link>
          );
        })}
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        className="w-full card p-4 flex items-center justify-center gap-3 text-red-600
                   hover:bg-red-50 transition-colors"
      >
        <LogOut size={20} />
        <span className="font-medium">Sign Out</span>
      </button>

      {/* Member Since */}
      <p className="text-center text-sm text-gray-400 mt-6">
        Member since {new Date(joinedAt).toLocaleDateString()}
      </p>
    </div>
  );
}
